"use client";

import Link from "next/link";
import { ArrowRight, Cpu, Telescope, ShieldCheck } from "lucide-react";
import { Section, Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { team as data } from "@/lib/data/about";

const iconMap = { Cpu, Telescope, ShieldCheck } as const;
const divisionAccents = [
  "var(--color-crimson)",
  "var(--color-purple)",
  "var(--color-emerald)",
] as const;

export function Team() {
  return (
    <Section id="team" className="relative">
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute top-1/3 right-1/4 h-72 w-72 rounded-full bg-[var(--color-purple)] opacity-15 blur-[120px]" />
      </div>

      <Container>
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow-tiny text-[var(--color-purple)]">{data.eyebrow}</span>
            <h2 className="mt-3">{data.title}</h2>
            <p className="mt-5 text-body text-[rgb(var(--fg-muted))] leading-relaxed">{data.intro}</p>
          </div>
        </Reveal>

        <div className="mt-12 grid lg:grid-cols-3 gap-5">
          {data.divisions.map((d, i) => {
            const Icon = iconMap[d.icon as keyof typeof iconMap] ?? Cpu;
            const accent = divisionAccents[i % divisionAccents.length]!;
            return (
              <Reveal key={d.title} delay={i * 100}>
                <div
                  className="glass rounded-2xl p-6 h-full"
                  style={{
                    boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 12%, transparent)`,
                  }}
                >
                  <span
                    className="grid place-items-center h-11 w-11 rounded-xl"
                    style={{
                      background: `color-mix(in oklch, ${accent} 18%, transparent)`,
                      boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 35%, transparent)`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color: accent }} />
                  </span>
                  <h3 className="mt-5">{d.title}</h3>
                  <p className="mt-3 text-sm text-[rgb(var(--fg-muted))] leading-relaxed">{d.body}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={400}>
          <div className="mt-10 glass rounded-2xl p-6">
            <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))]">Team by the Numbers</div>
            <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.byTheNumbers.map((n) => (
                <div key={n.label} className="text-center sm:text-left">
                  <div className="text-3xl font-bold text-[var(--color-crimson)]">{n.value}</div>
                  <div className="text-xs text-[rgb(var(--fg-muted))] mt-1 leading-snug">{n.label}</div>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={500}>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {data.cta.map((c, i) => (
              <Link
                key={c.label}
                href={c.href}
                className={
                  i === 0
                    ? "btn-primary-gradient h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                    : "btn-glass h-11 px-6 inline-flex items-center gap-2 rounded-full font-semibold text-sm"
                }
              >
                {c.label} <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </Reveal>
      </Container>
    </Section>
  );
}
